import { useEffect, useRef, useCallback } from "react";
import { getAccessToken } from "../api/auth/authService";
import { getWebSocketUrl } from "../api/config";
import type { Notification } from "../api/services/notification.type";

/** Server may send different formats - support common variants */
interface WebSocketMessage {
  type?: string;
  message_type?: string;
  event?: string;
  data?: Notification | { unread_count?: number; notification?: Notification };
  notification?: Notification;
  unread_count?: number;
}

interface UseNotificationWebSocketOptions {
  onNotification?: (notification: Notification) => void;
  onUnreadCountUpdate?: (count: number) => void;
  enabled?: boolean;
}

export const useNotificationWebSocket = ({
  onNotification,
  onUnreadCountUpdate,
  enabled = true,
}: UseNotificationWebSocketOptions = {}) => {
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const reconnectAttemptsRef = useRef(0);
  const isConnectingRef = useRef(false);
  const isMountedRef = useRef(true);
  const maxReconnectAttempts = 5;
  const reconnectDelay = 3000; // 3 seconds

  // Store callbacks and enabled in refs to avoid reconnecting when they change
  const onNotificationRef = useRef(onNotification);
  const onUnreadCountUpdateRef = useRef(onUnreadCountUpdate);
  const enabledRef = useRef(enabled);

  // Update refs when values change (without triggering reconnection)
  useEffect(() => {
    onNotificationRef.current = onNotification;
  }, [onNotification]);

  useEffect(() => {
    onUnreadCountUpdateRef.current = onUnreadCountUpdate;
  }, [onUnreadCountUpdate]);

  useEffect(() => {
    enabledRef.current = enabled;
  }, [enabled]);

  const connect = useCallback(() => {
    if (!enabledRef.current) {
      return;
    }

    const token = getAccessToken();
    if (!token) {
      console.warn("[WebSocket] ⏭️ Skipped: no access token");
      return;
    }

    if (isConnectingRef.current) {
      return;
    }

    if (wsRef.current?.readyState === WebSocket.OPEN) {
      return;
    }

    isConnectingRef.current = true;

    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }

    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }

    try {
      const wsUrl = getWebSocketUrl("/notifications/ws", token);
      const ws = new WebSocket(wsUrl);

      ws.onopen = () => {
        isConnectingRef.current = false;
        reconnectAttemptsRef.current = 0;
      };

      ws.onmessage = (event) => {
        try {
          const raw = JSON.parse(event.data) as Record<string, unknown>;
          const message = raw as WebSocketMessage;

          const dataObj = message.data as Record<string, unknown> | undefined;
          const notificationData =
            message.notification ??
            (message.data && typeof message.data === "object" && "id" in message.data
              ? (message.data as Notification)
              : dataObj?.notification) ??
            (raw && typeof raw === "object" && "id" in raw && "title" in raw
              ? (raw as unknown as Notification)
              : undefined);
          const unreadCount =
            typeof message.unread_count === "number"
              ? message.unread_count
              : typeof (message.data as Record<string, unknown>)?.unread_count ===
                  "number"
                ? (message.data as { unread_count: number }).unread_count
                : undefined;

          if (notificationData && onNotificationRef.current) {
            onNotificationRef.current(
              notificationData as unknown as Notification
            );
          }
          if (
            typeof unreadCount === "number" &&
            onUnreadCountUpdateRef.current
          ) {
            onUnreadCountUpdateRef.current(unreadCount);
          }

        } catch (error) {
          console.error("[WebSocket] Parse error:", error, "raw:", event.data);
        }
      };

      ws.onerror = (error) => {
        console.error("[WebSocket] ❌ Error event", {
          error,
          readyState: ws.readyState,
        });
      };

      ws.onclose = (event) => {
        isConnectingRef.current = false;
        wsRef.current = null;

        if (
          event.code !== 1000 &&
          isMountedRef.current &&
          enabledRef.current &&
          reconnectAttemptsRef.current < maxReconnectAttempts
        ) {
          reconnectAttemptsRef.current += 1;
          const delay =
            reconnectDelay * Math.pow(2, reconnectAttemptsRef.current - 1);

          reconnectTimeoutRef.current = setTimeout(() => {
            if (isMountedRef.current && enabledRef.current) {
              connect();
            }
          }, delay);
        }
      };

      wsRef.current = ws;
    } catch (error) {
      isConnectingRef.current = false;
      console.error("[WebSocket] Failed to create connection:", error);
    }
  }, []);

  const disconnect = useCallback(() => {
    isConnectingRef.current = false;

    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }

    if (wsRef.current) {
      wsRef.current.close(1000, "Manual disconnect");
      wsRef.current = null;
    }
  }, []);

  useEffect(() => {
    isMountedRef.current = true;

    if (!enabled) {
      disconnect();
      return;
    }

    connect();

    return () => {
      isMountedRef.current = false;
      disconnect();
    };
  }, [enabled, connect, disconnect]);

  return {
    connect,
    disconnect,
    isConnected: wsRef.current?.readyState === WebSocket.OPEN,
  };
};

